import {Link, useNavigate} from 'react-router-dom';
import {Spinner} from '@/components/ui/Spinner';
import {formatNumber} from '@tryghost/shade/utils';
import {useBillingInfo, useInitiateUpgrade, usePricing, useScheduleDowngrade, useValidateDowngrade} from '@/hooks/useBilling';
import {useState} from 'react';
import type {BillingPeriod, PricingPlan} from '@/types/api';

function formatPrice(dollars: number): string {
    if (dollars === 0) {
        return 'Free';
    }
    return `$${formatNumber(Math.round(dollars))}`;
}

function PeriodToggle({
    selected,
    onChange,
    hasLifetime
}: {
    selected: BillingPeriod;
    onChange: (period: BillingPeriod) => void;
    hasLifetime: boolean;
}) {
    const options: { value: BillingPeriod; label: string; badge?: string }[] = [
        {value: 'monthly', label: 'Monthly'},
        {value: 'yearly', label: 'Yearly', badge: 'Save ~17%'}
    ];
    if (hasLifetime) {
        options.push({value: 'lifetime', label: 'Lifetime'});
    }

    return (
        <div className="studio-panel"
            style={{display: 'inline-flex', background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', borderRadius: 14, padding: 4, gap: 2}}
        >
            {options.map(opt => (
                <button key={opt.value} className="studio-control studio-control-primary"
                    style={{cursor: 'pointer', fontFamily: 'inherit', position: 'relative', whiteSpace: 'nowrap', background: selected === opt.value ? 'var(--tt-accent)' : 'transparent', color: selected === opt.value ? '#fff' : 'var(--tt-text-secondary)'}}
                    type="button"
                    onClick={() => onChange(opt.value)}
                    onMouseEnter={(e) => {
                        if (selected !== opt.value) {
                            e.currentTarget.style.background = 'var(--tt-nav-hover-bg)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (selected !== opt.value) {
                            e.currentTarget.style.background = 'transparent';
                        }
                    }}
                >
                    {opt.label}
                    {opt.badge && (
                        <span
                            style={{
                                marginLeft: 6,
                                fontSize: '1.1rem',
                                color: selected === opt.value ? 'rgba(255,255,255,0.8)' : 'var(--tt-text-secondary)',
                                fontWeight: 600
                            }}
                        >
                            {opt.badge}
                        </span>
                    )}
                </button>
            ))}
        </div>
    );
}

function CheckIcon() {
    return (
        <svg fill="none" height="18" style={{flexShrink: 0, marginTop: 2}} viewBox="0 0 24 24" width="18">
            <path
                d="M20 6L9 17l-5-5"
                stroke="var(--tt-text-secondary)"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
            />
        </svg>
    );
}

function CrossIcon() {
    return (
        <svg fill="none" height="18" style={{flexShrink: 0, marginTop: 2}} viewBox="0 0 24 24" width="18">
            <path
                d="M18 6L6 18M6 6l12 12"
                stroke="var(--tt-text-tertiary)"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
            />
        </svg>
    );
}

const ALL_FEATURES = [
    {key: 'custom_domain', label: 'Custom domain'},
    {key: 'remove_branding', label: 'Remove branding'},
    {key: 'advanced_analytics', label: 'Advanced analytics'},
    {key: 'white_label_emails', label: 'White-label emails'},
    {key: 'api_access', label: 'API access'},
    {key: 'import_export', label: 'Import/Export'},
    {key: 'audit_logs', label: 'Audit logs'}
];

function PlanCard({
    plan,
    period,
    isCurrentPlan,
    onSelect,
    isLoading
}: {
    plan: PricingPlan;
    period: BillingPeriod;
    isCurrentPlan: boolean;
    onSelect: () => void;
    isLoading: boolean;
}) {
    // For lifetime-only plans, always show lifetime pricing regardless of selected period
    const isLifetimeOnly = plan.pricing.lifetime && !plan.pricing.monthly && !plan.pricing.yearly;
    const effectivePeriod = isLifetimeOnly ? 'lifetime' : period;
    const pricing = plan.pricing[effectivePeriod];
    const price = pricing?.amount || 0;

    // For lifetime-only plans, only available when viewing lifetime period
    // For other plans, available if they have a price for the selected period
    const isAvailable = isLifetimeOnly
        ? period === 'lifetime'
        : (price > 0 || (price === 0 && period === 'monthly'));

    return (
        <div className="studio-panel"
            style={{background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', borderRadius: 14, padding: 0, position: 'relative', display: 'flex', flexDirection: 'column', transition: 'border-color 0.2s ease', opacity: isAvailable ? 1 : 0.6}}
        >
            {/* Badge */}
            {plan.badge && !isCurrentPlan && (
                <div
                    style={{
                        position: 'absolute',
                        top: 20,
                        right: 20,
                        background: 'rgba(239, 90, 43, 0.1)',
                        color: 'var(--tt-accent)',
                        padding: '4px 12px',
                        borderRadius: 6,
                        fontSize: '1.15rem',
                        fontWeight: 600,
                        letterSpacing: '0.3px'
                    }}
                >
                    {plan.badge}
                </div>
            )}

            {/* Current Plan Badge */}
            {isCurrentPlan && (
                <div
                    style={{
                        position: 'absolute',
                        top: 20,
                        right: 20,
                        background: 'var(--studio-soft)',
                        color: 'var(--tt-text-secondary)',
                        padding: '4px 12px',
                        borderRadius: 6,
                        fontSize: '1.15rem',
                        fontWeight: 600,
                        letterSpacing: '0.3px'
                    }}
                >
                    Current
                </div>
            )}

            {/* Top section: header + pricing + CTA — fixed height so buttons align across cards */}
            <div style={{padding: '32px 32px 24px', height: 260, display: 'flex', flexDirection: 'column'}}>
                {/* Name */}
                <h3
                    style={{fontSize: '2rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: '0 0 8px 0', letterSpacing: '-.025em'}}
                >
                    {plan.name}
                </h3>
                {/* Description — fixed 2-line height */}
                <p
                    style={{
                        fontSize: '1.35rem',
                        color: 'var(--tt-text-secondary)',
                        margin: '0 0 20px 0',
                        lineHeight: 1.5,
                        height: '4.05rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical' as const,
                        overflow: 'hidden'
                    }}
                >
                    {plan.description || '\u00A0'}
                </p>

                {/* Pricing — grows to push button down */}
                <div style={{flex: 1}}>
                    {isAvailable ? (
                        <div>
                            <div style={{display: 'flex', alignItems: 'baseline', gap: 6}}>
                                <span
                                    style={{
                                        fontSize: '4.8rem',
                                        fontWeight: 800,
                                        color: 'var(--tt-text-primary)',
                                        lineHeight: 1,
                                        letterSpacing: '-0.02em'
                                    }}
                                >
                                    {formatPrice(price)}
                                </span>
                                <span
                                    style={{
                                        fontSize: '1.5rem',
                                        color: 'var(--tt-text-tertiary)',
                                        fontWeight: 500
                                    }}
                                >
                                    {effectivePeriod === 'monthly' && '/ mo'}
                                    {effectivePeriod === 'yearly' && '/ yr'}
                                    {effectivePeriod === 'lifetime' && ''}
                                </span>
                            </div>
                            <div
                                style={{
                                    fontSize: '1.3rem',
                                    color: 'var(--tt-text-tertiary)',
                                    marginTop: 6
                                }}
                            >
                                {effectivePeriod === 'yearly' && 'Billed annually'}
                                {effectivePeriod === 'monthly' && 'Billed monthly'}
                                {effectivePeriod === 'lifetime' && 'One-time payment'}
                            </div>
                        </div>
                    ) : (
                        <div
                            style={{
                                fontSize: '1.5rem',
                                color: 'var(--tt-text-tertiary)',
                                fontWeight: 600
                            }}
                        >
                        Not available for this billing period
                        </div>
                    )}
                </div>

                {/* CTA — always at bottom of top section */}
                <button className="studio-control studio-control-primary" disabled={isCurrentPlan || isLoading || !isAvailable}
                    style={{width: '100%', cursor: isCurrentPlan || isLoading || !isAvailable ? 'not-allowed' : 'pointer', fontFamily: 'inherit', flexShrink: 0, background: isCurrentPlan || !isAvailable ? 'var(--studio-soft)' : 'var(--tt-accent)', color: isCurrentPlan || !isAvailable ? 'var(--studio-muted)' : '#fff', opacity: isCurrentPlan || !isAvailable ? 1 : undefined}}
                    type="button"
                    onClick={onSelect}
                    onMouseEnter={(e) => {
                        if (isAvailable && !isCurrentPlan && !isLoading) {
                            e.currentTarget.style.background = '#d94f24';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (isAvailable && !isCurrentPlan) {
                            e.currentTarget.style.background = 'var(--tt-accent)';
                        }
                    }}
                >
                    {isCurrentPlan ? 'Current Plan' : !isAvailable ? 'Not Available' : isLoading ? 'Processing...' : 'Select plan'}
                </button>
            </div>

            {/* Divider */}
            <div style={{height: 1, background: 'var(--tt-divider)', margin: '0 32px'}} />

            {/* Features */}
            <div style={{padding: '24px 32px 32px', flex: 1}}>
                <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                    {plan.limits && (
                        <>
                            {plan.limits.staff !== undefined && (
                                <li style={featureStyle}>
                                    <CheckIcon />
                                    <span>
                                        <strong>{plan.limits.staff === 'Unlimited' || plan.limits.staff === -1 ? 'Unlimited' : typeof plan.limits.staff === 'number' ? formatNumber(plan.limits.staff) : plan.limits.staff}</strong> staff {plan.limits.staff === 1 ? 'user' : 'users'}
                                    </span>
                                </li>
                            )}
                            {plan.limits.members !== undefined && (
                                <li style={featureStyle}>
                                    <CheckIcon />
                                    <span>
                                        <strong>{plan.limits.members === 'Unlimited' || plan.limits.members === -1 ? 'Unlimited' : typeof plan.limits.members === 'number' ? formatNumber(plan.limits.members) : plan.limits.members}</strong> members
                                    </span>
                                </li>
                            )}
                            {plan.limits.sites !== undefined && (
                                <li style={featureStyle}>
                                    <CheckIcon />
                                    <span>
                                        <strong>{plan.limits.sites === 'Unlimited' || plan.limits.sites === -1 ? 'Unlimited' : typeof plan.limits.sites === 'number' ? formatNumber(plan.limits.sites) : plan.limits.sites}</strong> {plan.limits.sites === 1 ? 'publication' : 'publications'}
                                    </span>
                                </li>
                            )}
                            {plan.limits.newsletters !== undefined && (
                                <li style={featureStyle}>
                                    <CheckIcon />
                                    <span>
                                        <strong>{plan.limits.newsletters === 'Unlimited' || plan.limits.newsletters === -1 ? 'Unlimited' : typeof plan.limits.newsletters === 'number' ? formatNumber(plan.limits.newsletters) : plan.limits.newsletters}</strong> {plan.limits.newsletters === 1 ? 'newsletter' : 'newsletters'} per publication
                                    </span>
                                </li>
                            )}
                            {plan.limits.storage_mb !== undefined && (
                                <li style={featureStyle}>
                                    <CheckIcon />
                                    <span>
                                        <strong>{plan.limits.storage_mb === 'Unlimited' || plan.limits.storage_mb === -1 ? 'Unlimited' : typeof plan.limits.storage_mb === 'number' ? formatNumber(Math.round(plan.limits.storage_mb / 1000)) + ' GB' : plan.limits.storage_mb}</strong> storage
                                    </span>
                                </li>
                            )}
                            {plan.limits.email_sends_monthly !== undefined && (
                                <li style={featureStyle}>
                                    <CheckIcon />
                                    <span>
                                        <strong>{plan.limits.email_sends_monthly === 'Unlimited' || plan.limits.email_sends_monthly === -1 ? 'Unlimited' : typeof plan.limits.email_sends_monthly === 'number' ? formatNumber(plan.limits.email_sends_monthly) : plan.limits.email_sends_monthly}</strong> emails/mo
                                    </span>
                                </li>
                            )}
                        </>
                    )}

                    {plan.features && plan.features.length > 0 && (
                        <>
                            <li style={{height: 8}} />
                            {ALL_FEATURES.map((feature) => {
                                const isIncluded = plan.features && plan.features.includes(feature.key);
                                return (
                                    <li
                                        key={feature.key}
                                        style={{
                                            ...featureStyle,
                                            opacity: isIncluded ? 1 : 0.4
                                        }}
                                    >
                                        {isIncluded ? <CheckIcon /> : <CrossIcon />}
                                        <span>{feature.label}</span>
                                    </li>
                                );
                            })}
                        </>
                    )}
                </ul>
            </div>
        </div>
    );
}

const featureStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '6px 0',
    fontSize: '1.4rem',
    color: 'var(--tt-text-primary)',
    lineHeight: 1.5
};

function DowngradeWarningModal({
    violations,
    planName,
    onConfirm,
    onCancel,
    isLoading
}: {
    violations: Array<{ limit: string; current: number; allowed: number; message: string }>;
    planName: string;
    onConfirm: () => void;
    onCancel: () => void;
    isLoading: boolean;
}) {
    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--tt-modal-backdrop)'
            }}
            onClick={onCancel}
        >
            <div className="studio-panel"
                style={{background: 'var(--tt-card-bg)', borderRadius: 14, padding: 32, maxWidth: 500, width: '90%', maxHeight: '80vh', overflow: 'auto'}}
                onClick={e => e.stopPropagation()}
            >
                <div
                    style={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        background: 'rgba(245, 166, 35, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 20
                    }}
                >
                    <svg fill="none" height="28" stroke="#f5a623" strokeWidth="2" viewBox="0 0 24 24" width="28">
                        <path d="M12 9v4M12 17h.01" strokeLinecap="round" />
                        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                </div>

                <h3
                    style={{fontSize: '2rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: '0 0 8px 0', letterSpacing: '-.025em'}}
                >
                    Downgrade to {planName}?
                </h3>
                <p
                    style={{
                        fontSize: '1.4rem',
                        color: 'var(--tt-text-secondary)',
                        margin: '0 0 20px 0',
                        lineHeight: 1.5
                    }}
                >
                    Your current usage exceeds the limits of this plan. You&apos;ll need to reduce usage or some features may be restricted.
                </p>

                <div
                    style={{
                        background: 'var(--tt-bg)',
                        borderRadius: 12,
                        padding: 16,
                        marginBottom: 24
                    }}
                >
                    <h4
                        style={{
                            fontSize: '1.2rem',
                            fontWeight: 600,
                            color: 'var(--tt-text-secondary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                            margin: '0 0 12px 0'
                        }}
                    >
                        Limits Exceeded
                    </h4>
                    {violations.map((v, i) => (
                        <div
                            key={v.limit}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px 0',
                                borderBottom: i < violations.length - 1 ? '1px solid var(--tt-divider)' : 'none'
                            }}
                        >
                            <span style={{fontSize: '1.4rem', color: 'var(--tt-text-primary)'}}>
                                {v.limit.replace(/_/g, ' ')}
                            </span>
                            <span style={{fontSize: '1.3rem', color: 'var(--tt-red)'}}>
                                {v.current} → {v.allowed}
                            </span>
                        </div>
                    ))}
                </div>

                <div style={{display: 'flex', gap: 12}}>
                    <button className="studio-control" disabled={isLoading}
                        style={{flex: 1, height: 48, borderRadius: 8, background: 'transparent', border: '1px solid var(--tt-card-border)', color: 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit'}}
                        type="button"
                        onClick={onCancel}
                    >
                        Cancel
                    </button>
                    <button className="studio-control studio-control-primary" disabled={isLoading}
                        style={{flex: 1, cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.7 : 1, fontFamily: 'inherit'}}
                        type="button"
                        onClick={onConfirm}
                    >
                        {isLoading ? 'Processing...' : 'Schedule Downgrade'}
                    </button>
                </div>

                <p
                    style={{
                        fontSize: '1.2rem',
                        color: 'var(--tt-text-tertiary)',
                        margin: '16px 0 0 0',
                        textAlign: 'center'
                    }}
                >
                    The downgrade will take effect at the end of your current billing period.
                </p>
            </div>
        </div>
    );
}

export function UpgradePage() {
    const navigate = useNavigate();

    const [selectedPeriod, setSelectedPeriod] = useState<BillingPeriod>('monthly');
    const [downgradeWarning, setDowngradeWarning] = useState<{
        planId: string;
        planName: string;
        violations: Array<{ limit: string; current: number; allowed: number; message: string }>;
    } | null>(null);

    const {data: pricingData, isLoading: pricingLoading} = usePricing();
    const {data: billingInfo} = useBillingInfo();
    const upgradeMutation = useInitiateUpgrade();
    const validateDowngradeMutation = useValidateDowngrade();
    const scheduleDowngradeMutation = useScheduleDowngrade();

    // Get current plan from user's billing info (all sites share the same plan)
    const currentPlanId = billingInfo?.[0]?.plan_id;
    const plans = pricingData?.plans || [];
    const hasLifetime = plans.some(p => (p.pricing.lifetime?.amount ?? 0) > 0);

    const handleSelectPlan = async (plan: PricingPlan) => {
        // Check if this is a downgrade by comparing monthly prices
        const currentPlan = plans.find(p => p.id === currentPlanId);
        const currentPrice = currentPlan?.pricing?.monthly?.amount || currentPlan?.pricing?.yearly?.amount || currentPlan?.pricing?.lifetime?.amount || 0;
        const newPrice = plan.pricing?.monthly?.amount || plan.pricing?.yearly?.amount || plan.pricing?.lifetime?.amount || 0;

        if (newPrice < currentPrice) {
            // This is a downgrade, validate first
            try {
                const validation = await validateDowngradeMutation.mutateAsync({
                    planId: plan.id
                });

                if (!validation.canDowngrade && validation.violations.length > 0) {
                    // Has blocking violations — show warning
                    setDowngradeWarning({
                        planId: plan.id,
                        planName: plan.name,
                        violations: validation.violations
                    });
                    return;
                }

                // No blocking violations — show confirmation to schedule downgrade
                setDowngradeWarning({
                    planId: plan.id,
                    planName: plan.name,
                    violations: validation.violations || []
                });
            } catch {
            }
            return;
        }

        // Proceed with upgrade (higher plan)
        try {
            const result = await upgradeMutation.mutateAsync({
                planId: plan.id,
                billingPeriod: selectedPeriod
            });

            if (result.checkoutUrl) {
                window.location.href = result.checkoutUrl;
            } else {
                navigate('/billing');
            }
        } catch {
        }
    };

    const handleConfirmDowngrade = async () => {
        if (!downgradeWarning) {
            return;
        }

        try {
            await scheduleDowngradeMutation.mutateAsync({
                planId: downgradeWarning.planId
            });
            setDowngradeWarning(null);
            navigate('/billing');
        } catch {
        }
    };

    if (pricingLoading) {
        return (
            <div className="studio-content-page studio-plan-page" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--tt-bg)', maxWidth: 'none', margin: 0, fontFamily: 'inherit'}}>
                <Spinner />
            </div>
        );
    }

    return (
        <div className="studio-content-page studio-plan-page" style={{background: 'var(--tt-bg)', maxWidth: 'none', margin: 0, fontFamily: 'inherit'}}>
            {/* Header */}
            <div className="studio-plan-heading">
                <Link
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        color: 'var(--tt-text-secondary)',
                        fontSize: '1.4rem',
                        fontWeight: 500,
                        textDecoration: 'none',
                        marginBottom: 24,
                        transition: 'color 0.2s ease'
                    }}
                    to="/billing"
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--tt-accent)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--tt-text-secondary)'}
                >
                    <svg fill="none" height="16" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16">
                        <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Back to Billing
                </Link>
                <h1 className="studio-page-title">
                    Choose your plan
                </h1>
                <p
                    style={{
                        fontSize: '13px',
                        color: 'var(--tt-text-secondary)',
                        margin: '14px 0 28px 0',
                        lineHeight: 1.6,
                        fontWeight: 400
                    }}
                >
                    Select the plan that best fits your needs. Upgrade or downgrade anytime.
                </p>

                {/* Period Toggle */}
                <PeriodToggle
                    hasLifetime={hasLifetime}
                    selected={selectedPeriod}
                    onChange={setSelectedPeriod}
                />
            </div>

            <div className="studio-plan-grid">
                {plans.map(plan => (
                    <PlanCard
                        key={plan.id}
                        isCurrentPlan={plan.id === currentPlanId}
                        isLoading={upgradeMutation.isLoading || validateDowngradeMutation.isLoading}
                        period={selectedPeriod}
                        plan={plan}
                        onSelect={() => handleSelectPlan(plan)}
                    />
                ))}
            </div>

            {/* Footer Note */}
            <div style={{textAlign: 'center', marginTop: 80, maxWidth: 600, margin: '80px auto 0'}}>
                <p
                    style={{
                        fontSize: '1.4rem',
                        color: 'var(--tt-text-tertiary)',
                        lineHeight: 1.6
                    }}
                >
                    Need something custom?{' '}
                    <a
                        href="mailto:support@tribetale.com"
                        style={{
                            color: 'var(--tt-accent)',
                            textDecoration: 'none',
                            fontWeight: 600
                        }}
                    >
                        Contact us
                    </a>
                </p>
            </div>

            {/* Downgrade Warning Modal */}
            {downgradeWarning && (
                <DowngradeWarningModal
                    isLoading={scheduleDowngradeMutation.isLoading}
                    planName={downgradeWarning.planName}
                    violations={downgradeWarning.violations}
                    onCancel={() => setDowngradeWarning(null)}
                    onConfirm={handleConfirmDowngrade}
                />
            )}
        </div>
    );
}
